import type { FormOptions } from "../types";

export type AddressFormatLevel = {
  depth: number;
  name: string;
  isAddressLevel: boolean;
};

export type AddressFormat = {
  levels: ReadonlyArray<AddressFormatLevel>;
  postalCodeRegex: string;
  postalCodeExample: string;
};

export type AddressFormValues = {
  regionId: string;
  areaId: string;
  city: string;
  route: string;
  streetNumber: string;
  apartment: string;
};

export type AddressInputFields = {
  areaId: string;
  route: string;
  streetNumber: string;
  apartment: string;
  city: string;
};

export type AreaOptionSource = {
  id: string;
  name: string;
  parent: { name: string } | null;
};

export const EMPTY_ADDRESS_VALUES: AddressFormValues = {
  regionId: "",
  areaId: "",
  city: "",
  route: "",
  streetNumber: "",
  apartment: "",
};

export const getRegionLevel = (format: AddressFormat | null) =>
  format?.levels.find((level) => level.depth === 1) ?? null;

export const getAddressLevel = (format: AddressFormat | null) =>
  format?.levels.find((level) => level.isAddressLevel) ?? null;

/**
 * Whether the address points to an area below the top level. Countries that
 * only have a top level carry the city as free text instead.
 */
export const hasAreaBelowRegion = (format: AddressFormat | null): boolean =>
  (getAddressLevel(format)?.depth ?? 1) > 1;

const normalizeName = (name: string) => name.trim().toLocaleLowerCase();

/**
 * Select options for areas, naming the parent only when two areas share a
 * name (the same district name under different provinces, for instance).
 */
export const buildAreaOptions = (
  areas: ReadonlyArray<AreaOptionSource>,
): FormOptions[] => {
  const counts = new Map<string, number>();
  for (const area of areas) {
    const key = normalizeName(area.name);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return areas.map((area) => ({
    value: area.id,
    label:
      (counts.get(normalizeName(area.name)) ?? 0) > 1 && area.parent
        ? `${area.name} (${area.parent.name})`
        : area.name,
  }));
};

/** The fields the backend takes to create an address from the form values. */
export const toAddressInput = (
  values: AddressFormValues,
  format: AddressFormat | null,
): AddressInputFields => {
  const belowRegion = hasAreaBelowRegion(format);
  return {
    areaId: belowRegion ? values.areaId : values.regionId,
    route: values.route.trim(),
    streetNumber: values.streetNumber.trim(),
    apartment: values.apartment.trim(),
    city: belowRegion ? "" : values.city.trim(),
  };
};
